// src/hooks/useLoans.js
import { useState, useEffect } from 'react';
import loanService from '../services/loan.service';
import authService from '../services/auth.service';

export const useLoans = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchLoans = async () => {
    const userId = authService.getUserId();
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await loanService.getUserLoans(userId);
      // Transform backend data to match frontend format
      const transformedData = data.map(loan => ({
        id: loan.loanId || loan.id,
        borrower: loan.borrowerName,
        amount: loan.loanAmount,
        interestRate: loan.interestRate,
        dueDate: loan.dueDate,
        status: loan.status || 'active',
        notes: loan.notes
      }));
      setLoans(transformedData);
    } catch (err) {
      console.error('Error fetching loans:', err);
      setError(err.response?.data?.message || 'Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const addLoan = async (loanData) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const response = await loanService.createLoan(userId, loanData);
      await fetchLoans(); // Refresh the list
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to add loan';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const updateLoan = async (loanId, loanData) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      const response = await loanService.updateLoan(loanId, userId, loanData);
      await fetchLoans(); // Refresh the list
      return { success: true, data: response };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update loan';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const deleteLoan = async (loanId) => {
    const userId = authService.getUserId();
    if (!userId) return { success: false, error: 'User not authenticated' };

    setLoading(true);
    setError(null);
    try {
      await loanService.deleteLoan(loanId, userId);
      await fetchLoans(); // Refresh the list
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to delete loan';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getTotalLoans = () => {
    return loans.reduce((sum, loan) => sum + loan.amount, 0);
  };

  useEffect(() => {
    fetchLoans();
  }, []);

  return {
    loans,
    loading,
    error,
    fetchLoans,
    addLoan,
    updateLoan,
    deleteLoan,
    getTotalLoans
  };
};